import { useReducedMotion } from "framer-motion";
import { HolidayIcon } from "./HolidayDecor";

const seeded = (n) => {
  const x = Math.sin(n * 7919 + 1301) * 10000;
  return x - Math.floor(x);
};

function Lights({ colors }) {
  const bulbs = Array.from({ length: 11 }, (_, i) => ({ x: 20 + i * 36, y: 16 + Math.sin((i / 10) * Math.PI) * 16, color: colors[i % colors.length] }));
  return (
    <svg viewBox="0 0 400 48" width="100%" preserveAspectRatio="xMidYMin meet" className="absolute inset-x-0 top-0" aria-hidden="true">
      <path d="M0 6 Q200 44 400 6" fill="none" stroke="#1c1917" strokeWidth="1.6" opacity=".7" />
      {bulbs.map((b, i) => (
        <g key={i} className="holiday-twinkle" style={{ animationDelay: `${(i % 4) * -0.45}s`, transformOrigin: `${b.x}px ${b.y}px` }}>
          <rect x={b.x - 2.2} y={b.y - 1} width="4.4" height="4" rx="1" fill="#44403c" />
          <ellipse cx={b.x} cy={b.y + 9} rx="4.6" ry="6.4" fill={b.color} style={{ filter: `drop-shadow(0 0 4px ${b.color})` }} />
          <ellipse cx={b.x - 1.5} cy={b.y + 6.5} rx="1.2" ry="2" fill="rgba(255,255,255,.6)" />
        </g>
      ))}
    </svg>
  );
}

function Bunting({ colors }) {
  const flags = Array.from({ length: 9 }, (_, i) => ({ x: 10 + i * 42, y: 10 + Math.sin((i / 8) * Math.PI) * 12, color: colors[i % colors.length] }));
  return (
    <svg viewBox="0 0 400 48" width="100%" preserveAspectRatio="xMidYMin meet" className="absolute inset-x-0 top-0" aria-hidden="true">
      <path d="M-4 6 Q200 32 404 6" fill="none" stroke="#1c1917" strokeWidth="1.5" opacity=".65" />
      {flags.map((f, i) => (
        <path key={i} className="holiday-flag" style={{ animationDelay: `${i * -0.3}s`, transformOrigin: `${f.x + 14}px ${f.y}px` }} d={`M${f.x} ${f.y} L${f.x + 28} ${f.y} L${f.x + 14} ${f.y + 24} Z`} fill={f.color} stroke="rgba(0,0,0,.18)" strokeWidth=".8" />
      ))}
    </svg>
  );
}

function Cobweb({ mirror = false, animate = true }) {
  const rays = Array.from({ length: 7 }, (_, i) => (i * Math.PI) / 12);
  const pt = (r, a) => `${(r * Math.cos(a)).toFixed(1)} ${(r * Math.sin(a)).toFixed(1)}`;
  const rings = [28, 52, 76, 100].map((r) => rays.slice(0, -1).map((a, i) => `M${pt(r, a)} Q${pt(r * 0.9, (a + rays[i + 1]) / 2)} ${pt(r, rays[i + 1])}`).join(" ")).join(" ");
  const deg = (d) => (d * Math.PI) / 180;
  const crawl = `M${pt(100, deg(6))} A100 100 0 0 1 ${pt(100, deg(84))} L${pt(52, deg(84))} A52 52 0 0 0 ${pt(52, deg(12))} L${pt(76, deg(12))} A76 76 0 0 1 ${pt(76, deg(70))} L${pt(100, deg(70))} A100 100 0 0 0 ${pt(100, deg(6))}`;
  const id = `spider-path-${mirror ? "r" : "l"}`;
  return (
    <svg viewBox="0 0 110 110" width="34%" className={`absolute top-0 ${mirror ? "right-0 -scale-x-100" : "left-0"}`} aria-hidden="true" data-testid={`holiday-cobweb-${mirror ? "right" : "left"}`}>
      <g fill="none" stroke="rgba(255,255,255,.92)" strokeWidth="1.2" style={{ filter: "drop-shadow(0 0 1.5px rgba(28,25,23,.45))" }}>
        {rays.map((a, i) => <path key={i} d={`M0 0 L${pt(112, a)}`} />)}
        <path d={rings} />
      </g>
      <path id={id} d={crawl} fill="none" stroke="none" />
      <g data-testid={`holiday-spider-${mirror ? "right" : "left"}`} transform={animate ? undefined : "translate(72 40)"}>
        {animate && (
          <animateMotion dur={mirror ? "26s" : "21s"} begin={mirror ? "-9s" : "0s"} repeatCount="indefinite" rotate="auto" calcMode="linear">
            <mpath href={`#${id}`} />
          </animateMotion>
        )}
        <g transform={animate ? "rotate(90)" : undefined}>
          <circle cx="0" cy="3" r="4.2" fill="#F97316" stroke="#7c2d12" strokeWidth=".8" />
          <circle cx="0" cy="-2.2" r="2.5" fill="#F97316" stroke="#7c2d12" strokeWidth=".8" />
          <circle cx="-1" cy="-2.6" r=".6" fill="#1c1917" /><circle cx="1" cy="-2.6" r=".6" fill="#1c1917" />
          <path className={animate ? "spider-legs" : undefined} d="M-4 1 l-5 -3 M-4 4 l-5 2 M4 1 l5 -3 M4 4 l5 2" stroke="#F97316" strokeWidth="1.3" strokeLinecap="round" fill="none" style={{ filter: "drop-shadow(0 0 1px rgba(28,25,23,.6))" }} />
        </g>
      </g>
    </svg>
  );
}

function Snow({ colors }) {
  return (
    <>
      <svg viewBox="0 0 400 40" width="100%" preserveAspectRatio="none" className="absolute inset-x-0 top-0 h-7" aria-hidden="true">
        <path d="M0 0h400v14c-12 10-22 2-34 8s-20 10-32 2-24-2-36 6-22-4-34 2-20 10-32 2-24-4-36 4-24-2-36 6-22 2-34-4-26-6-38 4-20 6-30-2S20 32 0 20z" fill="rgba(255,255,255,.95)" style={{ filter: "drop-shadow(0 2px 2px rgba(37,99,235,.2))" }} />
      </svg>
      <HolidayIcon name="snowflake" color={colors[0]} size={44} className="holiday-spin absolute -left-3 -top-3 opacity-90" />
      <HolidayIcon name="snowflake" color={colors[1]} size={30} className="holiday-spin absolute -right-2 bottom-20 opacity-80" style={{ animationDirection: "reverse" }} />
    </>
  );
}

function Cluster({ theme }) {
  const spots = [
    { pos: "-left-3 -top-3", n: 3 },
    { pos: "-right-3 -top-2", n: 2 },
    { pos: "-left-2 bottom-20", n: 2 },
  ];
  return spots.map((s, si) => (
    <div key={si} className={`absolute flex items-start ${s.pos}`} aria-hidden="true">
      {Array.from({ length: s.n }, (_, i) => {
        const k = si * 5 + i;
        return (
          <HolidayIcon
            key={i}
            name={theme.icons[k % theme.icons.length]}
            color={theme.colors[k % theme.colors.length]}
            size={Math.round(26 + seeded(k) * 22)}
            className="holiday-bob drop-shadow-md"
            style={{ marginLeft: i ? -8 : 0, marginTop: i * 10, rotate: `${Math.round(seeded(k + 9) * 50 - 25)}deg`, animationDelay: `${-k * 0.6}s` }}
          />
        );
      })}
    </div>
  ));
}

export default function HolidayFrame({ theme }) {
  const reducedMotion = useReducedMotion();
  if (!theme) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-10" data-testid="holiday-frame" data-frame={theme.frame}>
      {theme.frame === "lights" && <Lights colors={theme.colors.filter((c) => c !== "#F8FAFC")} />}
      {theme.frame === "bunting" && <Bunting colors={theme.colors} />}
      {theme.frame === "cobweb" && <><Cobweb animate={!reducedMotion} /><Cobweb mirror animate={!reducedMotion} /></>}
      {theme.frame === "snow" && <Snow colors={theme.colors} />}
      {theme.frame === "cluster" && <Cluster theme={theme} />}
    </div>
  );
}
